// Centralized, strictly-typed analytics tracker.
// Wraps analytics providers (gtag, plausible, posthog) so callers get a single API.
// In non-prod, the function is exposed on `window.__TRACKING_SPY__` for Playwright spies.

type EventProps = Record<string, unknown>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    plausible?: (event: string, options?: { props?: EventProps }) => void;
    posthog?: { capture?: (event: string, props?: EventProps) => void };
    dataLayer?: unknown[];
    __TRACKING_SPY__?: typeof track;
  }
}

export type TrackedEvent =
  | "contact_submit"
  | "form_submit_success"
  | "click_email"
  | "click_whatsapp"
  | "click_chat"
  | "signup_success"
  | "login_success"
  | "password_reset_success"
  | "nav_anchor_click"
  | "sign_up_clicked"
  | "contact_clicked"
  | "contact_form_opened";

export interface TrackPayload {
  [key: string]: unknown;
  location?: string;
  anchor?: string;
  source?: string;
  email?: string;
}

/**
 * Lightweight, generic event tracker. Pushes to gtag, plausible, posthog, and GTM.
 */
export function trackEvent(name: string, props: EventProps = {}): void {
  if (typeof window === "undefined") return;
  try {
    window.gtag?.("event", name, props);
    window.plausible?.(name, { props });
    window.posthog?.capture?.(name, props);
    (window.dataLayer ||= []).push({ event: name, ...props });
    if (import.meta.env.DEV) {
      console.debug("[analytics]", name, props);
    }
  } catch {
    // Never let analytics break the app.
  }
}

/**
 * Strictly typed wrapper around trackEvent for conversion tracking.
 */
export function track(event: TrackedEvent, payload: TrackPayload = {}): void {
  trackEvent(event, payload);
}

/**
 * Only accept same-origin relative paths (must start with a single "/").
 * Rejects protocol-relative "//evil.com", absolute URLs, and backslash tricks.
 * Returns the fallback when the input is missing or unsafe.
 */
export function safeInternalPath(input: unknown, fallback = "/dashboard"): string {
  if (typeof input !== "string" || input.length === 0) return fallback;
  if (!input.startsWith("/")) return fallback;
  if (input.startsWith("//") || input.startsWith("/\\")) return fallback;
  if (/[\r\n\t]/.test(input)) return fallback;
  return input;
}

if (typeof window !== "undefined" && import.meta.env.MODE !== "production") {
  window.__TRACKING_SPY__ = track;
}
